import type { XREvent, XRSnapshot, XRState } from './types.js';

// Pure reducer. Returns same reference on no-op transitions so dispatch
// can short-circuit equality. Invariants:
//   - status='idle' → activeMode === null
//   - status='in-session' → modalOpen === false (closed by SESSION_STARTED)
//   - status='starting' is reachable only from 'modal-open' or 'error' (retry)
//   - CLOSE_MODAL during 'starting' is no-op (orphan session prevention)
export function xrTransition(state: XRState, event: XREvent): XRState {
  switch (event.type) {
    case 'OPEN_MODAL':
      if (state.status === 'in-session') return state;
      if (state.modalOpen) return state;
      return {
        ...state,
        status: 'detecting',
        modalOpen: true,
        activeMode: null,
        lastError: null,
      };
    case 'CLOSE_MODAL':
      if (state.status === 'in-session') return state;
      if (state.status === 'starting') return state;
      if (!state.modalOpen) return state;
      return {
        ...state,
        status: 'idle',
        modalOpen: false,
        activeMode: null,
        lastError: null,
      };
    case 'DETECT_RESULT':
      if (state.status !== 'detecting') return state;
      return { ...state, status: 'modal-open', support: event.support };
    case 'SESSION_REQUEST':
      if (state.status !== 'modal-open' && state.status !== 'error') return state;
      return { ...state, status: 'starting', activeMode: event.mode, lastError: null };
    case 'SESSION_STARTED':
      if (state.status !== 'starting') return state;
      return {
        ...state,
        status: 'in-session',
        modalOpen: false,
        activeMode: event.mode,
      };
    case 'SESSION_ENDED':
      if (state.status !== 'in-session' && state.status !== 'starting') return state;
      return {
        ...state,
        status: 'idle',
        modalOpen: false,
        activeMode: null,
        lastError: null,
      };
    case 'ERROR':
      return {
        ...state,
        status: 'error',
        modalOpen: true,
        activeMode: null,
        lastError: event.message,
      };
  }
}

export function xrSnapshot(state: XRState): XRSnapshot {
  return {
    status: state.status,
    modalOpen: state.modalOpen,
    support: state.support,
    activeMode: state.activeMode,
    lastError: state.lastError,
  };
}
